/**
 * Navigate docked beside the canvas, on a desktop (DEC-268, 2026-10-10).
 *
 * Operator: "on desktop, the canvas could be within the navigate ui. so in
 * the navigate ui, the canvas will have it's own page. that would bridge the
 * gap between the canvas and navigate. opbuttons logo shows and hides the
 * ui". Mocked in public/desktop-stage-study.html, then "looks good, let's go".
 *
 * THE MODEL. On a desktop Navigate is not a launcher that opens over the
 * canvas and closes to reveal it: its header and sidebar stay on screen, and
 * the canvas is its first section ("Canvas", above Home). The project's frame
 * is fitted into the space the sidebar and header leave. `navigateOpen()`
 * keeps its meaning everywhere it is read - a SECTION is showing over the
 * canvas - so "closing Navigate" is going back to the Canvas row, and every
 * caller that closes Navigate before opening something still works.
 *
 * The logo (Navigate's own, and the canvas bar's) hides and shows the
 * docked chrome; so does the \ key. Hidden, the canvas has the whole window.
 *
 * ONLY ON A DESKTOP: a real pointer, and a window with room for a 260px
 * sidebar beside a readable canvas. The iPad and the phone keep the
 * full-screen Navigate. A per-device setting (Settings > Look, "Navigate
 * beside the canvas") turns it off; the test bot launches with it off unless
 * a smoke opts in, so the suite keeps testing the launcher the iPad uses.
 *
 * Every value here is a per-device convenience in browser storage, wrapped:
 * private mode must not break the layout.
 */

import { createSignal } from 'solid-js';

/** The sidebar's width and the header's height while docked. The CSS uses
 *  the same two numbers (skin-components.css, `[data-docked]`). */
export const DOCK_SIDE_W = 260;
export const DOCK_HEAD_H = 50;
/** The smallest window that docks: the sidebar plus a canvas that is still
 *  worth playing. Below it Navigate is the full-screen launcher, as on an iPad. */
export const DOCK_MIN_W = 1000;
export const DOCK_MIN_H = 600;

const PREF_KEY = 'opbuttons.navigate.docked.v1';     // 'off' turns docking off on this device
const SHOWN_KEY = 'opbuttons.navigate.dockHidden.v1'; // '1' while the chrome is hidden
const ACTUAL_KEY = 'opbuttons.canvas.actualSize.v1';  // '1' while the canvas is at 100%

function read(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function write(key: string, value: string | null): void {
  try { if (value === null) localStorage.removeItem(key); else localStorage.setItem(key, value); } catch { /* convenience only */ }
}

/** A real pointer and a window big enough. App keeps it current on resize. */
const [capable, setCapable] = createSignal(false);
export const setDockCapable = setCapable;
export function measureDockCapable(w: number, h: number): boolean {
  const fine = typeof matchMedia === 'function' && matchMedia('(hover: hover) and (pointer: fine)').matches;
  return fine && w >= DOCK_MIN_W && h >= DOCK_MIN_H;
}

const [prefOn, setPrefOnSig] = createSignal(read(PREF_KEY) !== 'off');
/** Settings > Look: "Navigate beside the canvas". On unless turned off here. */
export const dockPref = prefOn;
export function setDockPref(on: boolean): void { setPrefOnSig(on); write(PREF_KEY, on ? null : 'off'); }

/** Navigate is docked on this screen (a desktop, and not turned off). */
export const navigateDocked = (): boolean => capable() && prefOn();

const [hidden, setHiddenSig] = createSignal(read(SHOWN_KEY) === '1');
/** The logo / \ toggle: the docked chrome is hidden and the canvas has the window. */
export const dockHidden = hidden;
export function setDockHidden(on: boolean): void { setHiddenSig(on); write(SHOWN_KEY, on ? '1' : null); }
/** The docked header and sidebar are on screen. */
export const dockChromeShown = (): boolean => navigateDocked() && !hidden();

const [actual, setActualSig] = createSignal(read(ACTUAL_KEY) === '1');
/** The canvas bar's size button (operator, 2026-10-10: "the fill button is
 *  supposed to switch between 100% size and fill the window"). Off (the
 *  default): the project's frame, its shape kept, scaled to fill the space
 *  Navigate leaves, past 100% where there is room. On: the frame at 100%,
 *  smaller only where the window cannot hold it. Desktop only; the iPad's
 *  canvas is its screen. */
export const canvasActualSize = actual;
export function setCanvasActualSize(on: boolean): void { setActualSig(on); write(ACTUAL_KEY, on ? '1' : null); }
